<script setup>
import { getI18n } from '@/plugins/i18n'

import { DxDataGrid } from 'devextreme-vue/data-grid'

import * as dataGrid from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'
import { usePortalStore } from '@/stores/portal.store'
import { useRegistryStore } from '@/stores/registry.store'

import ReferenceLink from '@/components/shared/ReferenceLink.vue'
import { menuRoutesEnum } from '@/router'

const props = defineProps({
  subject: {
    type: Object,
    required: true
  },
})

const gridId = 'subject-document-grid'

const i18n = getI18n()
const registryStore = useRegistryStore()
const portalStore = usePortalStore()
const { gridRef } = dataGrid.useGridRef()

const gridDataSource = dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.subjectDocumentsUrl,
  { filter: ['subject_ref', '=', props.subject.id] },
)

const columns = [
  {
    dataField: 'type',
    caption: i18n.t('common.documentType'),
    cellTemplate: 'typeCellTemplate',
    lookup: dataGrid.useLookup({ store: portalStore.getLocalizedDocumentTypes() }),
    alignment: 'center',
    width: '8rem',
  },
  dataGridColumn.useDateColumn(),
  dataGridColumn.useSeriesColumn(),
  dataGridColumn.useNumberMnemonicColumn(),
  dataGridColumn.useTotalNoVatColumn(),
  dataGridColumn.useTotalVatColumn(),
  {
    dataField: 'is_paid',
    caption: i18n.t('common.paid'),
    dataType: 'boolean',
  },
  {
    dataField: 'document_ref',
    cellTemplate: 'documentRef',
    caption: i18n.t('common.subjectDocument'),
    editable: false,
    sortable: false
  },
]

const onEditorPreparing = (e) => {
  if (e.parentType === 'filterRow' && e.dataType === 'boolean') {
    e.editorOptions.valueExpr = 'filterValue'
    e.editorOptions.displayExpr = 'filterText'
    e.editorOptions.dataSource = [
      { filterValue: null, filterText: i18n.t('common.all') },
      { filterValue: true, filterText: i18n.t('common.true') },
      { filterValue: false, filterText: i18n.t('common.false') },
      { filterValue: 'undefined', filterText: i18n.t('common.undefined') }
    ]
  }
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: {
    allowAdding: false,
    allowEditing: false,
    allowDeleting: false,
  },
  pager: { visible: false },
  columnChooser: { enabled: false },
  onEditorPreparing: onEditorPreparing,
  height: '100%',
})

const getReferenceLinkTarget = (data) => {
  switch (data.type) {
    case portalStore.documentTypesEnum.ORDER_SUPPLIER_ORDER:
      return menuRoutesEnum.SUPPLIER_ORDERS
    case portalStore.documentTypesEnum.GARAGE_QUOTE:
      return menuRoutesEnum.QUOTES
    case portalStore.documentTypesEnum.SALE_CORRESPONDENT:
      return menuRoutesEnum.SALE_COMMERCIAL
    case portalStore.documentTypesEnum.SALE_CREDIT_NOTE:
    case portalStore.documentTypesEnum.SALE_IMMEDIATE_INVOICE:
    case portalStore.documentTypesEnum.SALE_INVOICE:
      return menuRoutesEnum.SALE_INVOICES
    case portalStore.documentTypesEnum.SALE_DDT:
      return menuRoutesEnum.SALE_COMMERCIAL
    default:
      return menuRoutesEnum.WORKSHEETS
  }
}

</script>
<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
      class="q-mb-lg"
    >
      <template #documentRef="{ data: cellInfo }">
        <reference-link
          :data="{
            series: cellInfo.data.series,
            number: cellInfo.data.number_mnemonic
          }"
          :text-fields="['prefix', 'series', 'number']"
          :query-fields="['series', 'number']"
          :target="getReferenceLinkTarget(cellInfo.data)"
        />
      </template>
      <template #typeCellTemplate="{ data: templateOptions }">
        <q-chip
          size="sm"
          text-color="white"
          style="margin: 0"
          :label="portalStore.getLocalizedDocumentTypes().find(x => x.id === templateOptions.data.type)?.name"
          :style="{
            margin: 0,
            backgroundColor: portalStore.getLocalizedDocumentTypes().find(x => x.id === templateOptions.data.type)?.color
          }"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
